import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2, Upload } from "lucide-react";
import { api, fileUrl, formatApiError, imgSrc } from "../../lib/api";
import { money } from "../../lib/shop";
import { EMPTY_ITEM, ShopItemForm } from "./ShopItemForm";

const Designs = () => {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [file, setFile] = useState(null);
  useEffect(() => { api.get("/shop/designs").then((r) => setItems(r.data)); }, []);
  const add = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Choose a picture of the design");
    const fd = new FormData(); fd.append("name", name); fd.append("image", file);
    try { const { data } = await api.post("/admin/designs", fd); setItems([data, ...items]); setName(""); setFile(null); e.target.reset(); toast.success("Design added"); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const remove = async (d) => { if (!window.confirm(`Remove "${d.name}"?`)) return; await api.delete(`/admin/designs/${d.id}`); setItems(items.filter((x) => x.id !== d.id)); };
  return (
    <section className="space-y-4" data-testid="admin-designs">
      <h2 className="font-display text-2xl font-bold">Your designs</h2>
      <p className="text-sm text-black/55">Customers can tap one of these on any shop item, or describe their own.</p>
      <form onSubmit={add} className="flex flex-wrap items-end gap-3 rounded-2xl bg-white p-4 ring-1 ring-[#EBD08A]">
        <input required value={name} onChange={(e) => setName(e.target.value)} placeholder="Design name, e.g. USM Leopard" className="flex-1 rounded-xl border border-[#EBD08A] px-3 py-2.5" data-testid="design-name-input" />
        <input type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm" data-testid="design-file-input" />
        <button className="btn-pink !py-2.5" data-testid="design-add-btn"><Upload className="h-4 w-4" />Add design</button>
      </form>
      <div className="grid grid-cols-3 gap-3 sm:grid-cols-6">
        {items.map((d) => (
          <div key={d.id} className="overflow-hidden rounded-xl bg-white ring-1 ring-[#EBD08A]" data-testid={`admin-design-${d.id}`}>
            <img src={fileUrl(d.storage_path)} alt={d.name} className="aspect-square w-full object-cover" />
            <div className="flex items-center justify-between gap-1 px-2 py-1"><span className="truncate text-xs font-semibold">{d.name}</span>
              <button onClick={() => remove(d)} className="p-1 text-black/50 hover:text-red-600" aria-label="Remove design"><Trash2 className="h-3.5 w-3.5" /></button></div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default function AdminShop() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  useEffect(() => { api.get("/admin/shop").then((r) => setItems(r.data)); }, []);
  const saved = (it) => { setItems(items.some((x) => x.id === it.id) ? items.map((x) => (x.id === it.id ? it : x)) : [...items, it]); setEditing(null); };
  const remove = async (it) => { if (!window.confirm(`Delete "${it.name}"? Tip: you can hide it instead.`)) return; await api.delete(`/admin/shop/${it.id}`); setItems(items.filter((x) => x.id !== it.id)); };
  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><h1 className="font-display text-3xl font-bold">Shop</h1><p className="text-sm text-black/55">Set-price items customers pay for online. Custom work still goes through quotes.</p></div>
        {!editing && <button onClick={() => setEditing(EMPTY_ITEM)} className="btn-pink !py-2.5" data-testid="shop-item-new"><Plus className="h-4 w-4" />New item</button>}
      </div>
      {editing && <ShopItemForm key={editing.id || "new"} initial={editing} onSaved={saved} onCancel={() => setEditing(null)} />}
      <div className="space-y-3" data-testid="admin-shop-list">
        {items.map((it) => (
          <div key={it.id} className="flex items-center gap-4 rounded-2xl bg-white p-4 ring-1 ring-[#EBD08A]" data-testid={`admin-shop-item-${it.id}`}>
            {imgSrc(it) ? <img src={imgSrc(it)} alt="" className="h-14 w-14 rounded-xl object-cover" /> : <div className="h-14 w-14 rounded-xl bg-[#FFF4F8]" />}
            <div className="min-w-0 flex-1"><p className="truncate font-semibold">{it.name}</p><p className="text-sm text-black/55">{money(it.price)} · {it.pieces.length} piece(s) · {it.active ? "For sale" : "Hidden"}</p></div>
            <button onClick={() => setEditing(it)} className="rounded-full p-2 hover:bg-[#FFF4F8]" aria-label="Edit" data-testid={`admin-shop-edit-${it.id}`}><Pencil className="h-4 w-4" /></button>
            <button onClick={() => remove(it)} className="rounded-full p-2 text-black/50 hover:text-red-600" aria-label="Delete" data-testid={`admin-shop-delete-${it.id}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
      <Designs />
    </div>
  );
}
